import * as React from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { MapPin, Plus } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input, Label, NativeSelect } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { DataTable } from "@/components/data-table";
import { Mono, PageHeader, errorText } from "@/components/common";
import { api } from "@/lib/api";
import { slugify } from "@poji/shared";

interface Region {
  id: string;
  code: string;
  marketCode: string;
  name: string;
}
interface Locality {
  id: string;
  code: string;
  regionId: string;
  name: string;
  locales: string[];
  postcodePrefixes: string | null;
  latitude: number | null;
  longitude: number | null;
  isActive: boolean;
  providerCount: number;
  addressCount: number;
}
interface GeoData {
  regions: Region[];
  localities: Locality[];
}

const num = (s: string) => (s.trim() === "" ? null : Number(s.replace(",", ".")));

function LocalityDialog({ data, locality }: { data: GeoData; locality?: Locality }) {
  const qc = useQueryClient();
  const init = () => ({
    regionId: locality?.regionId ?? data.regions[0]?.id ?? "",
    name: "",
    code: "",
    postcode: locality?.postcodePrefixes ?? "",
    lat: locality?.latitude?.toString() ?? "",
    lng: locality?.longitude?.toString() ?? "",
  });
  const [f, setF] = React.useState(init);
  const set = (p: Partial<ReturnType<typeof init>>) => setF((x) => ({ ...x, ...p }));
  const code = locality ? locality.code : f.code || slugify(f.name);
  const lat = num(f.lat);
  const lng = num(f.lng);
  const postcode = f.postcode.trim().toUpperCase();
  const valid =
    (locality || (f.name.trim() && /^[a-z][a-z0-9-]{1,62}$/.test(code))) &&
    (lat === null || (lat >= -90 && lat <= 90)) &&
    (lng === null || (lng >= -180 && lng <= 180)) &&
    /^([A-Z]{2,4}(, ?[A-Z]{2,4})*)?$/.test(postcode);
  return (
    <ActionDialog
      onOpen={() => setF(init())}
      trigger={
        locality ? (
          <Button size="sm" variant="ghost">
            Düzenle
          </Button>
        ) : (
          <Button size="sm">
            <Plus /> Yerleşim yeri ekle
          </Button>
        )
      }
      title={locality ? `${locality.name}` : "Yerleşim yeri ekle"}
      description={
        locality
          ? "Posta kodu ön ekleri ve konum. Adı Çeviriler ekranından değişir."
          : "Ad varsayılan dilde (İngilizce); Maltacası Çeviriler ekranından. Kod ve site adresi addan üretilir, sonradan değişmez."
      }
      confirmLabel="Kaydet"
      canSubmit={Boolean(valid)}
      success="Kaydedildi."
      onSubmit={async (key) => {
        const body = { postcodePrefixes: postcode || null, latitude: lat, longitude: lng };
        if (locality)
          await api(`/v1/admin/geo/localities/${locality.id}`, { method: "PATCH", body });
        else
          await api("/v1/admin/geo/localities", {
            method: "POST",
            idempotencyKey: key,
            body: { regionId: f.regionId, code, name: f.name.trim(), ...body },
          });
        await qc.invalidateQueries({ queryKey: ["admin-geo"] });
      }}
    >
      <div className="grid gap-4">
        {!locality && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="g-region">Bölge</Label>
              <NativeSelect
                id="g-region"
                value={f.regionId}
                onChange={(e) => set({ regionId: e.target.value })}
              >
                {data.regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({r.marketCode})
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="g-name">Ad</Label>
              <Input id="g-name" value={f.name} onChange={(e) => set({ name: e.target.value })} />
              <p className="text-xs text-muted-foreground">
                Kod: <span className="font-mono">{code || "…"}</span>
              </p>
            </div>
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="grid gap-2">
            <Label htmlFor="g-post">Posta kodu ön eki</Label>
            <Input
              id="g-post"
              placeholder="ör. SLM"
              value={f.postcode}
              onChange={(e) => set({ postcode: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="g-lat">Enlem</Label>
            <Input
              id="g-lat"
              inputMode="decimal"
              value={f.lat}
              onChange={(e) => set({ lat: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="g-lng">Boylam</Label>
            <Input
              id="g-lng"
              inputMode="decimal"
              value={f.lng}
              onChange={(e) => set({ lng: e.target.value })}
            />
          </div>
        </div>
      </div>
    </ActionDialog>
  );
}

function ActiveToggle({ l }: { l: Locality }) {
  const qc = useQueryClient();
  return (
    <ActionDialog
      trigger={
        <Button size="sm" variant={l.isActive ? "ghost" : "outline"}>
          {l.isActive ? "Kapat" : "Aç"}
        </Button>
      }
      title={`${l.name}: ${l.isActive ? "kapat" : "aç"}`}
      description={
        l.isActive
          ? `Müşteri adres seçerken ve hizmet veren çalışma bölgesi seçerken görünmez; site sayfası üretilmez.${l.providerCount ? ` Şu an ${l.providerCount} hizmet veren burada çalışıyor; bölgeleri olduğu gibi kalır.` : ""}${l.addressCount ? ` ${l.addressCount} kayıtlı adres etkilenmez.` : ""}`
          : "Yeniden seçilebilir olur."
      }
      confirmLabel={l.isActive ? "Yeri kapat" : "Yeri aç"}
      confirmVariant={l.isActive ? "destructive" : "default"}
      success={l.isActive ? "Kapatıldı." : "Açıldı."}
      onSubmit={async () => {
        await api(`/v1/admin/geo/localities/${l.id}`, {
          method: "PATCH",
          body: { isActive: !l.isActive },
        }).catch((e) => {
          toast.error(errorText(e));
          throw e;
        });
        await qc.invalidateQueries({ queryKey: ["admin-geo"] });
      }}
    />
  );
}

export function GeoPage() {
  const q = useQuery({ queryKey: ["admin-geo"], queryFn: () => api<GeoData>("/v1/admin/geo") });
  const [region, setRegion] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [show, setShow] = React.useState<"all" | "active" | "inactive">("all");
  const data = q.data;
  const term = search.trim().toLowerCase();
  const rows = (data?.localities ?? []).filter(
    (l) =>
      (!region || l.regionId === region) &&
      (show === "all" || (show === "active") === l.isActive) &&
      (!term || `${l.name} ${l.code} ${l.postcodePrefixes ?? ""}`.toLowerCase().includes(term)),
  );
  const regionName = new Map((data?.regions ?? []).map((r) => [r.id, r.name]));

  const columns: ColumnDef<Locality, unknown>[] = [
    {
      id: "name",
      header: "Yerleşim yeri",
      accessorFn: (l) => l.name,
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.name}</div>
          <Mono>{row.original.code}</Mono>
        </div>
      ),
    },
    { id: "region", header: "Bölge", cell: ({ row }) => regionName.get(row.original.regionId) },
    {
      id: "postcode",
      header: "Posta kodu",
      cell: ({ row }) =>
        row.original.postcodePrefixes ?? <span className="text-muted-foreground">—</span>,
    },
    {
      id: "locales",
      header: "Diller",
      cell: ({ row }) => (
        <div className="flex gap-1">
          {row.original.locales.map((l) => (
            <Badge key={l} variant="outline">
              {l}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: "providers",
      header: "Hizmet veren",
      accessorFn: (l) => l.providerCount,
      cell: ({ row }) => <span className="tabular">{row.original.providerCount}</span>,
    },
    {
      id: "status",
      header: "Durum",
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge variant="success">Açık</Badge>
        ) : (
          <Badge variant="secondary">Kapalı</Badge>
        ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        data && (
          <div className="flex justify-end gap-1">
            <LocalityDialog data={data} locality={row.original} />
            <ActiveToggle l={row.original} />
          </div>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Bölgeler ve yerleşim yerleri"
        description={
          <>
            Müşterinin adresi ve hizmet verenin çalışma bölgesi bunlardan seçilir; site sayfaları
            (ör. “Sliema’da temizlik”) bunlara göre üretilir. Adlar{" "}
            <Link to="/translations" className="text-primary hover:underline">
              Çeviriler
            </Link>{" "}
            ekranından.
          </>
        }
        actions={data && <LocalityDialog data={data} />}
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {data?.regions.map((r) => {
          const ls = data.localities.filter((l) => l.regionId === r.id);
          return (
            <Card key={r.id}>
              <CardContent className="flex items-center gap-3 p-4">
                <div className="grid size-9 place-items-center rounded-lg bg-primary/10 text-primary">
                  <MapPin className="size-[18px]" />
                </div>
                <div className="text-sm">
                  <div className="font-medium">
                    {r.name} <span className="text-muted-foreground">({r.marketCode})</span>
                  </div>
                  <div className="text-muted-foreground">
                    {ls.filter((l) => l.isActive).length}/{ls.length} yerleşim yeri açık
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <div className="mb-3 flex flex-wrap gap-2">
        <Input
          aria-label="Ara"
          placeholder="Ad, kod ya da posta kodu"
          className="w-56"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <NativeSelect aria-label="Bölge" value={region} onChange={(e) => setRegion(e.target.value)}>
          <option value="">Tüm bölgeler</option>
          {data?.regions.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label="Durum"
          value={show}
          onChange={(e) => setShow(e.target.value as typeof show)}
        >
          <option value="all">Açık ve kapalı</option>
          <option value="active">Yalnızca açık</option>
          <option value="inactive">Yalnızca kapalı</option>
        </NativeSelect>
      </div>
      <DataTable
        columns={columns}
        data={data ? rows : undefined}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="Bu süzgece uyan yer yok."
      />
    </>
  );
}
